import React, { useEffect, useState, useMemo } from "react";
import * as S from "./style";
import {
  Package,
  ClipboardCheck,
  PackageSearch,
  Truck,
  Box,
  CheckCircle,
  MapPin,
  Home,
  XCircle,
} from "lucide-react";
import { useWebSocket } from "@hooks/useWebSocket";

interface OrderStepperProps {
  currentStatus: string;
  orderId: number;
  isPharma?: boolean;
}

// The first three steps are part of the prescription flow and only apply to
// pharmacy orders. Non-pharma orders start straight at "Order Placed".
const prescriptionStepKeys = [
  "Prescription Requested",
  "Prescription Accepted",
  "Invoice Shared",
];

const statusSteps = [
  {
    key: "Prescription Requested",
    label: "Prescription",
    icon: ClipboardCheck,
  },
  { key: "Prescription Accepted", label: "Accepted", icon: CheckCircle },
  { key: "Invoice Shared", label: "Invoice", icon: Box },
  { key: "Order Placed", label: "Placed", icon: Package },

  { key: "Order Confirmed", label: "Confirmed", icon: ClipboardCheck },
  { key: "Order Processing", label: "Processing", icon: PackageSearch },
  { key: "Delivery Assigned", label: "Assigned", icon: Truck },
  { key: "Ready for Pickup", label: "Ready", icon: Box },
  { key: "Order Picked Up", label: "Picked Up", icon: Box },
  { key: "Out for Delivery", label: "Out for Delivery", icon: Truck },
  { key: "Near Customer", label: "Near Customer", icon: MapPin },
  { key: "Delivered", label: "Delivered", icon: Home },
];

const OrderStepper: React.FC<OrderStepperProps> = ({
  currentStatus,
  orderId,
  isPharma = false,
}) => {
  const [activeStatus, setActiveStatus] = useState(currentStatus);
  const { socket } = useWebSocket();

  const steps = useMemo(
    () =>
      isPharma
        ? statusSteps
        : statusSteps.filter(
            (step) => !prescriptionStepKeys.includes(step.key),
          ),
    [isPharma],
  );

  useEffect(() => {
    setActiveStatus(currentStatus);
  }, [currentStatus]);

  useEffect(() => {
    if (!socket) return;

    const handleStatusUpdate = (data: any) => {
      // Check if the event is for this order
      // Assuming payload has id or orderId. Adjust based on actual event payload.
      if (data?.id === orderId || data?.orderId === orderId) {
        if (data.status) {
          setActiveStatus(data.status);
        }
      }
    };

    // Listen to generic order update events or specific status change events
    // Based on useOrderEvents.tsx, 'order:updated' seems relevant
    socket.on("order:updated", handleStatusUpdate);
    socket.on("order:status:changed", handleStatusUpdate); // Potential event name

    return () => {
      socket.off("order:updated", handleStatusUpdate);
      socket.off("order:status:changed", handleStatusUpdate);
    };
  }, [socket, orderId]);

  const currentIndex = useMemo(() => {
    if (activeStatus === "Cancelled" || activeStatus === "Rejected") return -1;
    return steps.findIndex((step) => step.key === activeStatus);
  }, [activeStatus, steps]);

  const isCancelled =
    activeStatus === "Cancelled" || activeStatus === "Rejected";

  const progress =
    currentIndex === -1 ? 0 : (currentIndex / (steps.length - 1)) * 100;

  return (
    <S.StepperContainer>
      {isCancelled ? (
        <div
          style={{
            textAlign: "center",
            padding: "20px",
            color: "#ef4444",
            fontWeight: "bold",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <XCircle size={48} />
          <span>Order is Cancelled</span>
        </div>
      ) : (
        <S.StepperTrack>
          <S.ProgressLineBackground />
          <S.ProgressLineFill
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          />
          {steps.map((step, index) => {
            const isCompleted = index <= currentIndex;
            const isActive = index === currentIndex;
            const Icon = step.icon;

            return (
              <S.StepItem key={step.key}>
                <S.IconWrapper
                  active={isActive}
                  completed={isCompleted}
                  animate={{
                    scale: isActive ? 1.2 : 1,
                    backgroundColor: isCompleted ? "#0d9488" : "#ffffff",
                    borderColor: isCompleted ? "#0d9488" : "#e5e7eb",
                  }}
                  transition={{ duration: 0.3 }}
                >
                  {isCompleted ? <CheckCircle size={20} /> : <Icon size={20} />}
                </S.IconWrapper>
                <S.StepLabel active={isActive} completed={isCompleted}>
                  {step.label}
                </S.StepLabel>
              </S.StepItem>
            );
          })}
        </S.StepperTrack>
      )}
    </S.StepperContainer>
  );
};

export default OrderStepper;
